import { useState, useCallback, memo, type ComponentProps } from "react";

import { MeetingEndedState, MeetingParticipantRoles } from "graphql_globals";
import { captureException } from "util/exception";
import SelectedDevicesController from "common/selected_devices_controller";
import AudioOnlyParty from "common/meeting/sidebar/audio_only_party";
import TitleAgentParty from "common/meeting/sidebar/title_agent_party";
import AttorneyParty from "common/meeting/sidebar/attorney_party";
import RealtorParty from "common/meeting/sidebar/realtor_party";
import BrokerParty from "common/meeting/sidebar/broker_party";
import PublicSpectatorParty from "common/meeting/sidebar/public_spectator_party";
import VideoConference from "common/video_conference";
import AudioVideoSettingsContainer from "common/video_conference/audio_video_settings/meeting_container";
import BackgroundSettings, {
  BackgroundSettingsContainer,
} from "common/video_conference/background_settings";
import MeetingSidebarContainer from "common/meeting/sidebar/container";
import type { UpdateNotaryProfileSetting_updateNotaryProfileSetting_notaryProfile as NotaryProfile } from "common/tech_check/video/update_notary_profile_setting_mutation.graphql";

import SidebarFooter from "./footer";
import SignerParty from "./signer_party";
import CredibleWitnessParty from "./credible_witness_party";
import WitnessParty from "./witness_party";
import NotaryLocalParty from "./local_party";
import type { NotaryMeetingSidebar as Meeting } from "./index_fragment.graphql";

type LocalPartyProps = ComponentProps<typeof NotaryLocalParty>;
type SignerPartyProps = ComponentProps<typeof SignerParty>;
type CredPartyProps = ComponentProps<typeof CredibleWitnessParty>;
type WitnessPartyProps = ComponentProps<typeof WitnessParty>;
type LocalParty = LocalPartyProps["party"] & { role?: undefined };
type KnownRemoteParty =
  | ({ role: MeetingParticipantRoles.SIGNER } & SignerPartyProps["party"])
  | ({ role: MeetingParticipantRoles.AUDIO_ONLY } & ComponentProps<typeof AudioOnlyParty>["party"])
  | ({ role: MeetingParticipantRoles.ATTORNEY } & ComponentProps<typeof AttorneyParty>["party"])
  | ({ role: MeetingParticipantRoles.TITLE_AGENT } & ComponentProps<
      typeof TitleAgentParty
    >["party"])
  | ({ role: MeetingParticipantRoles.REALTOR } & ComponentProps<typeof RealtorParty>["party"])
  | ({ role: MeetingParticipantRoles.BROKER } & ComponentProps<typeof BrokerParty>["party"])
  | ({ role: MeetingParticipantRoles.PUBLIC_SPECTATOR } & ComponentProps<
      typeof PublicSpectatorParty
    >["party"])
  | ({ role: MeetingParticipantRoles.WITNESS } & WitnessPartyProps["party"])
  | ({ role: MeetingParticipantRoles.CREDIBLE_WITNESS } & CredPartyProps["party"]);
type RemoteParty = { id: string } & (
  | { role: Exclude<MeetingParticipantRoles, KnownRemoteParty["role"]> }
  | KnownRemoteParty
);
type NotaryUser = Exclude<WitnessPartyProps["notaryUser"], null | undefined> & {
  notaryProfile: NotaryProfile;
};
type PartyProps = {
  party: RemoteParty;
  meeting: Meeting;
  notaryUser: NotaryUser;
  onCheckId: (
    participant:
      | Parameters<SignerPartyProps["onCheckId"]>[0]
      | Parameters<CredPartyProps["onCheckId"]>[0],
  ) => void;
  onSetPenholder: LocalPartyProps["onSetPenholder"];
  signerConnectionState: SignerPartyProps["signerConnectionState"];
};
type Props = {
  user: PartyProps["notaryUser"] & { id: string };
  meeting: Meeting;
  onCheckId: PartyProps["onCheckId"];
  onSetPenholder: PartyProps["onSetPenholder"];
  onToggleInstructions: ComponentProps<typeof SidebarFooter>["onToggleInstructions"];
  signerConnectionState: PartyProps["signerConnectionState"];
};

function renderParty({
  party,
  meeting,
  onCheckId,
  onSetPenholder,
  signerConnectionState,
  notaryUser,
}: PartyProps) {
  switch (party.role) {
    case MeetingParticipantRoles.SIGNER:
      return (
        <SignerParty
          key={party.id}
          party={party}
          meeting={meeting}
          onCheckId={onCheckId}
          onSetPenholder={onSetPenholder}
          signerConnectionState={signerConnectionState}
        />
      );
    case MeetingParticipantRoles.CREDIBLE_WITNESS:
      return (
        <CredibleWitnessParty
          key={party.id}
          party={party}
          meeting={meeting}
          onCheckId={onCheckId}
        />
      );
    case MeetingParticipantRoles.AUDIO_ONLY:
      return <AudioOnlyParty key={party.id} party={party} />;
    case MeetingParticipantRoles.TITLE_AGENT:
      return <TitleAgentParty key={party.id} party={party} />;
    case MeetingParticipantRoles.ATTORNEY:
      return <AttorneyParty key={party.id} party={party} />;
    case MeetingParticipantRoles.WITNESS:
      return (
        <WitnessParty key={party.id} notaryUser={notaryUser} party={party} meeting={meeting} />
      );
    case MeetingParticipantRoles.REALTOR:
      return <RealtorParty key={party.id} party={party} />;
    case MeetingParticipantRoles.BROKER:
      return <BrokerParty key={party.id} party={party} />;
    case MeetingParticipantRoles.PUBLIC_SPECTATOR:
      return <PublicSpectatorParty key={party.id} party={party} />;
    case MeetingParticipantRoles.SPECTATOR:
      return null; // Dont show admin spectators
    default:
      captureException(new Error(`Unexpected participant role ${party.role}`));
      return null;
  }
}

function NotarySidebar({
  user,
  meeting,
  onCheckId,
  onSetPenholder,
  onToggleInstructions,
  signerConnectionState,
}: Props) {
  const { endedState, documentBundle } = meeting;
  const [muted, setMuted] = useState(false);
  const toggleMute = useCallback(() => setMuted((m) => !m), []);
  const [showAVSettings, setAVSettings] = useState(false);
  const [showBackgroundSettings, setShowBackgroundSettings] = useState(false);
  const toggleAVSettings = useCallback(() => {
    setShowBackgroundSettings(false);
    setAVSettings((av) => !av);
  }, []);
  const toggleBackgroundSettings = () => {
    setShowBackgroundSettings((show) => !show);
    setAVSettings(false);
  };
  const meetingStillRunning = endedState === MeetingEndedState.NOT_COMPLETED;
  const footer = documentBundle!.instructions.length > 0 && (
    <SidebarFooter onToggleInstructions={onToggleInstructions} />
  );
  return (
    <MeetingSidebarContainer footer={footer}>
      <SelectedDevicesController>
        {({ onChangeDevices, selectedDevices }) => (
          <VideoConference<LocalParty, RemoteParty>
            meeting={meeting}
            user={user}
            muted={muted}
            publishAudio
            publishVideo
            selectedDevices={selectedDevices}
          >
            {({ localParty, remoteParties }) => (
              <>
                {meetingStillRunning &&
                  remoteParties.map((party) =>
                    renderParty({
                      party,
                      meeting,
                      onCheckId,
                      onSetPenholder,
                      signerConnectionState,
                      notaryUser: user,
                    }),
                  )}
                <NotaryLocalParty
                  meeting={meeting}
                  party={localParty}
                  onToggleAVSettings={toggleAVSettings}
                  onToggleBackgroundSettings={toggleBackgroundSettings}
                  muted={muted}
                  onToggleMute={toggleMute}
                  onSetPenholder={onSetPenholder}
                />
                {showAVSettings && (
                  <AudioVideoSettingsContainer
                    onClose={toggleAVSettings}
                    selectedDevices={selectedDevices}
                    onChangeDevices={onChangeDevices}
                  />
                )}
                {showBackgroundSettings && (
                  <BackgroundSettingsContainer
                    onToggleBackgroundSettings={toggleBackgroundSettings}
                  >
                    <BackgroundSettings notaryProfile={user.notaryProfile} />
                  </BackgroundSettingsContainer>
                )}
              </>
            )}
          </VideoConference>
        )}
      </SelectedDevicesController>
    </MeetingSidebarContainer>
  );
}

export default memo(NotarySidebar);
